import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import StorefrontLayout from "../shared/StorefrontLayout";
import { t } from "../../i18n";

const FALLBACK_ART = "/assets/fallback-art.jpg";
const EMPTY_CATEGORY_ICON = "/assets/feature-icons/no-products.svg";

function flattenNavTree(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.flatMap((item) => [item, ...flattenNavTree(item?.children)]);
}

function parseCategoryIdFromCategoryText(value) {
  const raw = String(value || "").trim();
  const match = /^category-(\d+)$/i.exec(raw);
  if (match) {
    return Number(match[1]);
  }

  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    return asNumber;
  }

  return 0;
}

function parseProductGroupId(product) {
  const sourceGroupId = Number(product?.sourceGroupId || 0);
  if (Number.isFinite(sourceGroupId) && sourceGroupId > 0) {
    return sourceGroupId;
  }

  const rawPath = String(product?.categoryPath || "").trim();
  if (/^\d+$/.test(rawPath)) {
    return Number(rawPath);
  }

  return parseCategoryIdFromCategoryText(product?.category);
}

function parsePathGroupId(path) {
  const rawPath = String(path || "").trim();
  if (!rawPath || !rawPath.startsWith("/products")) {
    return 0;
  }

  const queryString = rawPath.includes("?") ? rawPath.split("?")[1] : "";
  const params = new URLSearchParams(queryString || "");
  const groupId = Number(params.get("groupId") || params.get("categoryId") || 0);
  return Number.isFinite(groupId) && groupId > 0 ? groupId : 0;
}

function parsePathGroupLabel(path) {
  const rawPath = String(path || "").trim();
  if (!rawPath || !rawPath.startsWith("/products")) {
    return "";
  }

  const queryString = rawPath.includes("?") ? rawPath.split("?")[1] : "";
  const params = new URLSearchParams(queryString || "");
  return String(params.get("group") || params.get("category") || "").trim();
}

function formatPrice(value, currency) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2
  }).format(value);
}

export default function ProductsPage({ apiBaseUrl, navItems = [], storefrontLocale = "en", pageTemplateConfig = {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState("USD");
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showPricing, setShowPricing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [groupStats, setGroupStats] = useState([]);
  const [groupCountById, setGroupCountById] = useState(() => new Map());
  const [othersCount, setOthersCount] = useState(0);

  const groupStatsById = useMemo(() => {
    const mapping = new Map();
    groupStats.forEach((item) => {
      const groupId = Number(item?.groupId || 0);
      if (!Number.isFinite(groupId) || groupId <= 0 || mapping.has(groupId)) {
        return;
      }
      mapping.set(groupId, item);
    });
    return mapping;
  }, [groupStats]);

  const categoryTree = useMemo(() => {
    const productsRoot = flattenNavTree(navItems).find((item) => String(item?.key || "").trim() === "products") || null;
    const productNavChildren = Array.isArray(productsRoot?.children) ? productsRoot.children : [];

    function buildNodes(items, depth = 1, inheritedParentGroupId = 0) {
      if (!Array.isArray(items) || depth > 3) {
        return [];
      }

      return items.map((item, index) => {
        const label = String(item?.label || "").trim();
        const rawPath = String(item?.path || "").trim();
        const groupId = parsePathGroupId(rawPath);
        if (!label || !Number.isFinite(groupId) || groupId <= 0) {
          return null;
        }

        const groupStat = groupStatsById.get(groupId);
        const queryString = rawPath.includes("?") ? rawPath.split("?")[1] : "";
        const params = new URLSearchParams(queryString || "");
        params.set("groupId", String(groupId));
        params.set("group", label);

        const hasMenuChildren = Array.isArray(item?.children) && item.children.length > 0;
        const directCount = Number(groupStat?.directCount || 0);
        const totalCount = Number(groupStat?.totalCount || groupCountById.get(groupId) || 0);
        if (hasMenuChildren || (directCount > 0 && totalCount > directCount)) {
          params.set("includeDescendants", "1");
        } else {
          params.delete("includeDescendants");
        }

        const children = buildNodes(item?.children, depth + 1, groupId);
        const node = {
          key: String(item?.key || `group-${groupId}-${index}`).trim() || `group-${groupId}-${index}`,
          groupId,
          parentGroupId: Number(groupStat?.parentGroupId || inheritedParentGroupId || 0),
          label,
          path: `/products?${params.toString()}`,
          totalCount,
          directCount,
          iconImage: String(item?.iconImage || groupStat?.iconImage || "").trim(),
          previewImageUrl: String(groupStat?.previewImageUrl || "").trim()
        };

        return children.length ? { ...node, children } : node;
      }).filter(Boolean);
    }

    return buildNodes(productNavChildren);
  }, [groupCountById, groupStatsById, navItems]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams(location.search);
        const keyword = (params.get("keyword") || "").trim();
        const groupId = Number(params.get("groupId") || 0);
        const group = String(params.get("group") || "").trim();
        const includeDescendants = ["1", "true", "yes", "on"].includes(String(params.get("includeDescendants") || "").trim().toLowerCase());
        const requestParams = new URLSearchParams();
        requestParams.set("lang", storefrontLocale);
        requestParams.set("currency", currency);
        if (keyword) {
          requestParams.set("keyword", keyword);
        }
        if (Number.isFinite(groupId) && groupId > 0) {
          requestParams.set("groupId", String(groupId));
          if (includeDescendants) {
            requestParams.set("includeDescendants", "1");
          }
        } else if (group) {
          requestParams.set("group", group);
        }

        const response = await fetch(
          `${apiBaseUrl}/products?${requestParams.toString()}`
        );
        if (!response.ok) {
          throw new Error(t("error"));
        }
        const data = await response.json();
        if (!cancelled) {
          const nextProducts = Array.isArray(data?.products) ? data.products : [];
          const groupStats = Array.isArray(data?.meta?.groupStats) ? data.meta.groupStats : [];
          const nextOthersCount = Number(data?.meta?.othersCount || 0);
          const nextGroupCounts = new Map();
          groupStats.forEach((item) => {
            const groupId = Number(item?.groupId || 0);
            if (!Number.isFinite(groupId) || groupId <= 0) {
              return;
            }

            const totalCount = Number(item?.totalCount || 0);
            nextGroupCounts.set(groupId, Number.isFinite(totalCount) && totalCount > 0 ? totalCount : 0);
          });

          setProducts(nextProducts);
          setGroupStats(groupStats);
          setGroupCountById(nextGroupCounts);
          setOthersCount(Number.isFinite(nextOthersCount) && nextOthersCount > 0 ? nextOthersCount : 0);
          setShowPricing(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setGroupStats([]);
          setGroupCountById(new Map());
          setOthersCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, storefrontLocale, currency, location.search]);

  const categoryLabelById = useMemo(() => {
    const mapping = new Map();

    groupStats.forEach((item) => {
      const categoryId = Number(item?.groupId || 0);
      const label = String(item?.groupNameEn || "").trim();
      if (!categoryId || !label) {
        return;
      }

      if (!mapping.has(categoryId)) {
        mapping.set(categoryId, label);
      }
    });

    return mapping;
  }, [groupStats]);

  const categorizedProducts = products.map((item) => {
    const rawCategory = item.category || "Others";
    const categoryId = parseProductGroupId(item);
    const mappedCategory = categoryId ? categoryLabelById.get(categoryId) : "";

    return {
      ...item,
      groupId: categoryId,
      category: mappedCategory || rawCategory
    };
  });

  const categoryPreviewById = useMemo(() => {
    const mapping = new Map();

    function visit(items) {
      if (!Array.isArray(items)) {
        return;
      }

      items.forEach((item) => {
        const groupId = Number(item?.groupId || 0);
        const totalCount = Number(item?.totalCount || 0);
        const directCount = Number(item?.directCount || 0);
        const hasProducts = totalCount > 0 || directCount > 0;
        const imageUrl = hasProducts
          ? String(item?.iconImage || "").trim() || String(item?.previewImageUrl || "").trim() || EMPTY_CATEGORY_ICON
          : EMPTY_CATEGORY_ICON;
        if (Number.isFinite(groupId) && groupId > 0 && !mapping.has(groupId) && imageUrl) {
          mapping.set(groupId, imageUrl);
        }

        if (Array.isArray(item?.children) && item.children.length) {
          visit(item.children);
        }
      });
    }

    visit(categoryTree);

    return mapping;
  }, [categoryTree]);

  const totalVisibleProductCount = useMemo(() => {
    const groupedCount = categoryTree.reduce((sum, node) => {
      const nodeGroupId = parsePathGroupId(node?.path);
      const nextValue = Number(groupCountById.get(nodeGroupId) || 0);
      return sum + (Number.isFinite(nextValue) && nextValue > 0 ? nextValue : 0);
    }, 0);

    const ungroupedCount = Number(othersCount || 0);
    return groupedCount + (Number.isFinite(ungroupedCount) && ungroupedCount > 0 ? ungroupedCount : 0);
  }, [categoryTree, groupCountById, othersCount]);

  const requestedCategory = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requested = String(params.get("group") || "").trim();
    if (requested) {
      return requested;
    }

    const categoryId = Number(params.get("groupId") || 0);
    if (Number.isFinite(categoryId) && categoryId > 0) {
      return categoryLabelById.get(categoryId) || `Group-${categoryId}`;
    }

    return "All Products";
  }, [location.search, categoryLabelById]);

  const activeTreeState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requestedCategoryName = String(params.get("group") || "").trim().toLowerCase();
    const requestedCategoryId = Number(params.get("groupId") || 0);

    function walk(items, pathKeys = []) {
      for (let index = 0; index < items.length; index += 1) {
        const node = items[index];
        const nodePathKey = [...pathKeys, `${node.key}-${index}`];
        const nodeCategoryId = parsePathGroupId(node.path);
        const nodeCategoryLabel = parsePathGroupLabel(node.path).toLowerCase();
        const nodeLabel = String(node.label || "").trim().toLowerCase();

        const matchedById = Number.isFinite(requestedCategoryId) && requestedCategoryId > 0 && nodeCategoryId === requestedCategoryId;
        const matchedByName = requestedCategoryName && (nodeCategoryLabel === requestedCategoryName || nodeLabel === requestedCategoryName);

        if (matchedById || matchedByName) {
          return { activeNodeKey: nodePathKey[nodePathKey.length - 1], expandedPathKeys: new Set(nodePathKey) };
        }

        if (Array.isArray(node.children) && node.children.length) {
          const nested = walk(node.children, nodePathKey);
          if (nested) {
            return nested;
          }
        }
      }
      return null;
    }

    return walk(categoryTree) || { activeNodeKey: "", expandedPathKeys: new Set() };
  }, [categoryTree, location.search]);

  const requestedKeyword = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("keyword") || "").trim();
  }, [location.search]);

  const requestedSort = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const nextSort = String(params.get("sort") || "default").trim().toLowerCase();
    return ["name-asc", "name-desc"].includes(nextSort) ? nextSort : "default";
  }, [location.search]);

  const [searchInput, setSearchInput] = useState("");

  const activeCategory = requestedCategory || "All Products";
  const isOtherGroupActive = ["other", "others"].includes(String(activeCategory || "").trim().toLowerCase());

  const hasCategoryFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requestedGroupId = Number(params.get("groupId") || 0);
    const requestedGroupLabel = String(params.get("group") || "").trim();
    return (Number.isFinite(requestedGroupId) && requestedGroupId > 0) || Boolean(requestedGroupLabel);
  }, [location.search]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const brandTitle = t("metaTitleBrand");
    const allProductsTitle = t("productsAll");
    const contentTitle = hasCategoryFilter
      ? String(activeCategory || "").trim() || allProductsTitle
      : allProductsTitle;

    document.title = `${contentTitle} | ${brandTitle}`;
  }, [activeCategory, hasCategoryFilter, storefrontLocale]);

  const categoryMatchedProducts = useMemo(() => {
    return categorizedProducts;
  }, [categorizedProducts]);

  const sortedCategoryProducts = useMemo(() => {
    if (requestedSort === "default") {
      return categoryMatchedProducts;
    }

    const nextProducts = [...categoryMatchedProducts];
    nextProducts.sort((left, right) => {
      const leftName = String(left?.name || "").trim();
      const rightName = String(right?.name || "").trim();
      const compared = leftName.localeCompare(rightName, undefined, { sensitivity: "base", numeric: true });
      return requestedSort === "name-desc" ? compared * -1 : compared;
    });
    return nextProducts;
  }, [categoryMatchedProducts, requestedSort]);

  const PAGE_SIZE = 12;
  const totalPages = Math.max(1, Math.ceil(sortedCategoryProducts.length / PAGE_SIZE));
  const displayedProducts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedCategoryProducts.slice(start, start + PAGE_SIZE);
  }, [sortedCategoryProducts, currentPage]);
  const visibleRangeStart = sortedCategoryProducts.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const visibleRangeEnd = sortedCategoryProducts.length ? Math.min(currentPage * PAGE_SIZE, sortedCategoryProducts.length) : 0;
  const paginationItems = useMemo(() => {
    if (totalPages <= 1) {
      return [];
    }

    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const normalizedPages = Array.from(pages)
      .filter((page) => Number.isFinite(page) && page >= 1 && page <= totalPages)
      .sort((left, right) => left - right);

    return normalizedPages.reduce((items, page, index) => {
      const previous = normalizedPages[index - 1];
      if (typeof previous === "number" && page - previous > 1) {
        items.push({ type: "ellipsis", key: `ellipsis-${previous}-${page}` });
      }
      items.push({ type: "page", key: `page-${page}`, value: page });
      return items;
    }, []);
  }, [currentPage, totalPages]);

  useEffect(() => {
    setSearchInput(requestedKeyword);
  }, [requestedKeyword]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, requestedKeyword, requestedSort]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  function buildProductsSearch(params) {
    const nextSearch = params.toString();
    return nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname;
  }

  function createPreservedParams() {
    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams();
    const keyword = String(currentParams.get("keyword") || "").trim();
    const sort = String(currentParams.get("sort") || "default").trim().toLowerCase();
    const includeDescendants = String(currentParams.get("includeDescendants") || "").trim().toLowerCase();

    if (keyword) {
      nextParams.set("keyword", keyword);
    }
    if (["name-asc", "name-desc"].includes(sort)) {
      nextParams.set("sort", sort);
    }
    if (["1", "true", "yes", "on"].includes(includeDescendants)) {
      nextParams.set("includeDescendants", "1");
    }

    return nextParams;
  }

  function handleSearchSubmit(event) {
    event.preventDefault();

    const params = new URLSearchParams(location.search);
    const keyword = String(searchInput || "").trim();
    if (keyword) {
      params.set("keyword", keyword);
    } else {
      params.delete("keyword");
    }
    navigate(buildProductsSearch(params));
  }

  function handleSortChange(event) {
    const nextSort = String(event.target.value || "default").trim().toLowerCase();
    const params = new URLSearchParams(location.search);

    if (["name-asc", "name-desc"].includes(nextSort)) {
      params.set("sort", nextSort);
    } else {
      params.delete("sort");
    }

    navigate(buildProductsSearch(params));
  }

  function navigateToCategoryPath(path) {
    const targetPath = String(path || "").trim() || "/products";
    const [pathname] = targetPath.split("?");
    const nextParams = createPreservedParams();
    const pathGroupId = parsePathGroupId(targetPath);
    const pathGroupLabel = parsePathGroupLabel(targetPath);
    if (pathGroupId > 0) {
      nextParams.set("groupId", String(pathGroupId));
    }
    if (pathGroupLabel) {
      nextParams.set("group", pathGroupLabel);
    }
    const pathQueryString = targetPath.includes("?") ? targetPath.split("?")[1] : "";
    const pathParams = new URLSearchParams(pathQueryString || "");
    if (["1", "true", "yes", "on"].includes(String(pathParams.get("includeDescendants") || "").trim().toLowerCase())) {
      nextParams.set("includeDescendants", "1");
    } else {
      nextParams.delete("includeDescendants");
    }

    navigate(nextParams.toString() ? `${pathname}?${nextParams.toString()}` : pathname);
  }

  function handleAllProductsClick() {
    if (activeCategory === "All Products") {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
      return;
    }

    const nextParams = createPreservedParams();
    nextParams.delete("groupId");
    nextParams.delete("group");
    nextParams.delete("includeDescendants");
    navigate(buildProductsSearch(nextParams));
  }

  function handleOtherGroupClick() {
    const nextParams = createPreservedParams();
    nextParams.delete("groupId");
    nextParams.set("group", "Others");
    nextParams.delete("includeDescendants");

    navigate(buildProductsSearch(nextParams));
  }

  function renderCategoryTree(items, level = 1, pathKeys = []) {
    if (!Array.isArray(items) || !items.length) {
      return null;
    }

    return (
      <ul className={`market-tree-level market-tree-level-${level}`}>
        {items.map((node, index) => {
          const nodePathKeys = [...pathKeys, `${node.key}-${index}`];
          const nodeKey = nodePathKeys[nodePathKeys.length - 1];
          const hasChildren = Array.isArray(node.children) && node.children.length > 0;
          const isExpanded = hasChildren && activeTreeState.expandedPathKeys.has(nodeKey);
          const isActive = activeTreeState.activeNodeKey === nodeKey;
          const nodeGroupId = parsePathGroupId(node.path);
          const nodeCount = groupCountById.get(nodeGroupId);
          const nodePreviewImage = categoryPreviewById.get(nodeGroupId) || "";

          return (
            <li key={nodeKey} className={`market-tree-item level-${level}${isActive ? " is-active" : ""}${isExpanded ? " is-expanded" : ""}`}>
              <button
                type="button"
                className={`market-tree-button${nodePreviewImage ? "" : " has-no-thumb"}`}
                onClick={() => navigateToCategoryPath(node.path)}
                aria-current={isActive ? "page" : undefined}
                aria-expanded={hasChildren ? isExpanded : undefined}
              >
                {nodePreviewImage ? (
                  <span className="market-tree-thumb" aria-hidden="true">
                    <img
                      src={nodePreviewImage}
                      alt=""
                      loading="lazy"
                      onError={(event) => {
                        if (event.currentTarget.src.endsWith(EMPTY_CATEGORY_ICON)) {
                          return;
                        }
                        event.currentTarget.src = EMPTY_CATEGORY_ICON;
                      }}
                    />
                  </span>
                ) : null}
                <span className="market-tree-copy">
                  <span className="market-tree-label">{node.label}</span>
                </span>
                {typeof nodeCount === "number" ? <span className="market-tree-count">{nodeCount}</span> : null}
                {hasChildren ? <span className="market-tree-caret" aria-hidden="true">{isExpanded ? "▾" : "▸"}</span> : null}
              </button>
              {hasChildren && isExpanded ? renderCategoryTree(node.children, level + 1, nodePathKeys) : null}
            </li>
          );
        })}
      </ul>
    );
  }

  function handleContactNow(product) {
    if (!product) {
      navigate("/contact");
      return;
    }

    const params = new URLSearchParams({
      product: product.name || "",
      sku: product.sku || "",
      category: product.category || "",
      source: "products"
    });

    navigate(`/contact?${params.toString()}`);
  }

  function buildProductLinkState(item) {
    const itemGroupId = parseCategoryIdFromCategoryText(item?.category || "");
    const itemGroupLabel = itemGroupId > 0 ? String(categoryLabelById.get(itemGroupId) || "").trim() : "";
    const breadcrumbGroup = activeCategory !== "All Products" ? activeCategory : itemGroupLabel;
    return {
      productTitle: item?.name || "",
      breadcrumbGroup
    };
  }

  const sectionVisibility = pageTemplateConfig?.sectionVisibility || {};
  const isCatalogSectionVisible = (sectionKey) => sectionVisibility?.[sectionKey] !== false;

  return (
    <StorefrontLayout
      apiBaseUrl={apiBaseUrl}
      currency={currency}
      onChangeCurrency={setCurrency}
      showPricing={showPricing}
    >
      <section className="market-products-page">
        {isCatalogSectionVisible("filters") ? (
        <aside className="market-left-sidebar">
          <div className="market-category-card market-category-tree-card">
            <div className="market-category-card-head">
              <h3>{t("productsCategoryTitle")}</h3>
              <p>{t("categoryBody")}</p>
            </div>
            <div className="market-category-tree-root">
              <button
                type="button"
                className={`market-all-products-entry has-no-thumb${activeCategory === "All Products" ? " is-active" : ""}`}
                onClick={handleAllProductsClick}
                aria-current={activeCategory === "All Products" ? "page" : undefined}
              >
                <span className="market-tree-copy">
                  <span className="market-tree-label">{t("productsAll")}</span>
                </span>
                <span className="market-tree-count">{totalVisibleProductCount}</span>
              </button>
              <nav aria-label={t("productsCategoryNavigationAria")} className="market-category-tree-nav">
                {renderCategoryTree(categoryTree)}
              </nav>
              <button
                type="button"
                className={`market-all-products-entry has-no-thumb${isOtherGroupActive ? " is-active" : ""}`}
                onClick={handleOtherGroupClick}
                aria-current={isOtherGroupActive ? "page" : undefined}
              >
                <span className="market-tree-copy">
                  <span className="market-tree-label">{t("productsOthers")}</span>
                </span>
                <span className="market-tree-count">{othersCount}</span>
              </button>
            </div>
          </div>
        </aside>
        ) : null}

        <div className="market-right-content">
          {isCatalogSectionVisible("hero") ? (
          <header className="market-head">
            <div className="market-head-copy">
              <p className="loya-product-kicker">{t("productsCategoryTitle")}</p>
              <h2>{activeCategory}</h2>
              {String(pageTemplateConfig?.heroDescription || "").trim() ? (
                <p>{String(pageTemplateConfig?.heroDescription || "").trim()}</p>
              ) : null}
            </div>
            <div className="market-head-meta">
              <div className="market-toolbar" role="search">
                <form className="market-search-form" onSubmit={handleSearchSubmit}>
                  <input
                    type="search"
                    className="market-search-box"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder={t("ariaSearchProductByName")}
                    aria-label={t("ariaSearchProductByName")}
                  />
                  <button type="submit" className="market-search-submit">{t("ariaSearch")}</button>
                </form>
                <label className="market-sort-box" aria-label={t("ariaSortProducts")}>
                  <span>{t("sortLabel")}</span>
                  <select value={requestedSort} onChange={handleSortChange}>
                    <option value="default">{t("sortDefault")}</option>
                    <option value="name-asc">A-Z</option>
                    <option value="name-desc">Z-A</option>
                  </select>
                </label>
              </div>
              <div className="market-results-meta">
                <span className="market-results-pill">{sortedCategoryProducts.length}</span>
                <span className="market-results-label">{t("viewAll")}</span>
              </div>
            </div>
          </header>
          ) : null}

          {loading ? <p className="state">{t("loading")}</p> : null}
          {error ? <p className="state error">{error}</p> : null}

          {!loading && !error ? (
            <>
              {isCatalogSectionVisible("productGrid") ? (
              <section className="market-product-grid">
                {displayedProducts.map((item) => (
                  <article key={item.id} className="market-product-card">
                    <div className="market-media">
                      <Link
                        to={`/products/${item.id}`}
                        state={buildProductLinkState(item)}
                        className="image-link"
                        aria-label={item.name}
                      >
                        <img
                          src={item.imageUrl || FALLBACK_ART}
                          alt={item.name}
                          loading="lazy"
                          onError={(event) => {
                            if (event.currentTarget.src.endsWith(FALLBACK_ART)) {
                              return;
                            }
                            event.currentTarget.src = FALLBACK_ART;
                          }}
                        />
                      </Link>
                    </div>
                    <div className="market-product-body">
                      <Link
                        to={`/products/${item.id}`}
                        state={buildProductLinkState(item)}
                        className="market-name-link"
                        title={item.name}
                        aria-label={item.name}
                      >
                        <p className="market-name">{item.name}</p>
                      </Link>
                      <p className="market-meta">{item.category || activeCategory}</p>
                      {showPricing ? (
                        <p className="market-price">{formatPrice(item.price, item.currency)}</p>
                      ) : null}
                      <button type="button" className="market-capability" onClick={() => handleContactNow(item)}>{String(pageTemplateConfig?.ctaText || t("productsContactNow")).trim()}</button>
                    </div>
                  </article>
                ))}

                {!displayedProducts.length ? (
                  <p className="state">{String(pageTemplateConfig?.emptyMessage || t("productsEmpty")).trim()}</p>
                ) : null}
              </section>
              ) : null}

              {isCatalogSectionVisible("pagination") && sortedCategoryProducts.length > PAGE_SIZE ? (
                <nav className="market-pagination" aria-label={t("productsPaginationAria")}>
                  <button
                    type="button"
                    className="market-pagination-arrow"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("productsPrev")}
                  </button>
                  <div className="market-pagination-pages" aria-label={t("productsPaginationAria")}> 
                    {paginationItems.map((item) => {
                      if (item.type === "ellipsis") {
                        return <span key={item.key} className="market-pagination-ellipsis" aria-hidden="true">...</span>;
                      }

                      return (
                        <button
                          key={item.key}
                          type="button"
                          className={`market-pagination-page${item.value === currentPage ? " is-active" : ""}`}
                          onClick={() => setCurrentPage(item.value)}
                          aria-current={item.value === currentPage ? "page" : undefined}
                        >
                          {item.value}
                        </button>
                      );
                    })}
                  </div>
                  <span className="market-pagination-status" aria-live="polite">
                    <strong>{currentPage} / {totalPages}</strong>
                    <em>{t("productsPage")}</em>
                  </span>
                  <span className="market-pagination-range">
                    {visibleRangeStart}-{visibleRangeEnd} / {sortedCategoryProducts.length}
                  </span>
                  <button
                    type="button"
                    className="market-pagination-arrow"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("productsNext")}
                  </button>
                </nav>
              ) : null}
            </>
          ) : null}
        </div>
      </section>
    </StorefrontLayout>
  );
}
